<script setup>
/**
 * FormControlContentPageUse
 * Manages content propagation across different pages.
 * Optimized for Vue 2.7 Composition API while being "weeks away" from Vue 3.
 */
import {computed} from 'vue'
import PagePath from '@/common/PagePath.vue'
import Button from '@/common/Button.vue'
import api from '@/services/api'
import {usePageContentDataStore} from '@/stores/pageContentData'
import {usePageEditorStore} from '@/stores/pageEditor'
import {useModalsStore} from '@/stores/modals'
import {useFormStore} from '@/stores/form'
import {useRoute, useRouter} from "vue-router"

const props = defineProps({
    // Standard Orchestrator Props
    formId: {
        type: String,
        required: true
    },
    field: {
        type: Object,
        required: true
    },
    currentData: {
        type: Object,
        required: true
    },
    // Orchestrator provides 'value', though logic uses currentData.id
    value: {
        type: [String, Number, Array],
        default: null
    }
})

const router = useRouter()
const route = useRoute()

// Pinia Stores
const pageContentDataStore = usePageContentDataStore()
const pageEditorStore = usePageEditorStore()
const modalsStore = useModalsStore()
const formStore = computed(() => props.formId ? useFormStore(props.formId) : null)

// --- Computed ---

const contentId = computed(() => props.currentData.id)

const items = computed(() => {
    return (pageContentDataStore.items || [])
        .filter(o => o.contentId === contentId.value)
})

const formSmall = computed(() => formStore.value?.formSmall)

const isSiteTree = computed(() => route.name === 'siteTree')

const isPageEditor = computed(() => route.name === 'pageEditor')

// --- Methods ---

/**
 * Handles navigation to the specific page/content context
 */
const goToPage = (pageId) => {
    if (isPageEditor.value) {
        pageEditorStore.goToContent({
            router: router,
            pageContentId: items.value.find(o => o.pageId === pageId)?.id,
            pageId: pageId
        })
    } else if (isSiteTree.value) {
        router.push({name: 'siteTree', params: {pageOrContentId: pageId}})
    } else {
        router.push({name: 'form', params: {component: 'pages', id: pageId}})
    }
}

/**
 * Opens the SiteTree modal and handles the diffing logic for content propagation
 */
const showSiteTree = () => {
    modalsStore.show({
        componentName: 'SiteTreeModal',
        obj: {
            propagatedContentId: contentId.value,
            onSave: async (data) => {
                const propagationData = data.propagationData
                const newPageIds = propagationData.map(o => o.pageId)

                const originalPages = items.value
                const originalPageIds = originalPages.map(o => o.pageId)

                const addedPages = propagationData.filter(o => !originalPageIds.includes(o.pageId))
                const removedPages = originalPages.filter(o => !newPageIds.includes(o.pageId))

                // Sequential Additions
                for (const o of addedPages) {
                    const pageSectionLinkId = o.contentArea.startsWith('area_')
                        ? parseInt(o.contentArea.split('_')[1])
                        : null

                    await api.post('api/save-content-to-page', {
                        pageId: o.pageId,
                        pageSectionLinkId,
                        contentArea: o.contentArea,
                        contentId: contentId.value
                    })
                }

                // Sequential Removals
                for (const o of removedPages) {
                    try {
                        await api.delete(`api/delete-content-from-page/${o.pageId}/${contentId.value}`)
                    } catch (err) {
                        // Handle error if needed
                    }
                }

                // Refresh the global page content state
                await pageContentDataStore.init()
            }
        }
    })
}
</script>

<template>
    <div>
        <ul class="list-group mb-2">
            <li
                v-for="item in items"
                :key="item.id"
                class="list-group-item d-flex align-items-center"
                :class="{'py-1 px-2 small': formSmall}"
            >
                <div class="flex-fill">
                    <PagePath :page-id="item.pageId" class="me-2"/>
                    {{ item.contentArea }}
                </div>

                <Button class="btn-sm" @click="goToPage(item.pageId)">
                    <i class="bi-pencil-square"/>
                </Button>
            </li>
        </ul>

        <Button @click="showSiteTree" class="btn-sm border">
            <i class="bi bi-plus-lg"/>
        </Button>
    </div>
</template>